import { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Plus, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useVendors, useCreateVendor, type Vendor } from "@/hooks/use-vendors";

interface VendorComboboxProps {
  businessId: number;
  value: number | null;
  onChange: (vendorId: number | null) => void;
  placeholder?: string;
}

export function VendorCombobox({ businessId, value, onChange, placeholder }: VendorComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const { data: vendors = [], isLoading, refetch } = useVendors(businessId);
  const createVendor = useCreateVendor();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync display name when value changes externally
  useEffect(() => {
    if (value && vendors.length > 0) {
      const found = vendors.find((v) => Number(v.id) === Number(value));
      if (found) setDisplayName(found.name);
    } else if (!value) {
      setDisplayName(null);
    }
  }, [value, vendors]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = search.trim()
    ? vendors.filter((v) => v.name.toLowerCase().includes(search.trim().toLowerCase()))
    : vendors;

  const hasExactMatch = vendors.some((v) => v.name.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (vendor: Vendor) => {
    setDisplayName(vendor.name);
    onChange(vendor.id);
    setSearch("");
    setOpen(false);
  };

  const handleClear = () => {
    setDisplayName(null);
    onChange(null);
    setSearch("");
    setOpen(false);
  };

  const handleCreateVendor = async () => {
    if (!search.trim()) return;
    try {
      const newVendor = await createVendor.mutateAsync({
        name: search.trim(),
        businessId,
        status: "active",
      });
      await refetch();
      setDisplayName(newVendor.name);
      onChange(newVendor.id);
      setSearch("");
      setOpen(false);
    } catch {
      // Error handled by mutation onError
    }
  };

  const buttonLabel = displayName || placeholder || "Select vendor...";

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Dropdown rendered inline (no Portal = no focus trap conflict with Sheet) */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors..."
              className="border-0 shadow-none focus-visible:ring-0 h-10 px-0"
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter" && filtered.length === 1) {
                  handleSelect(filtered[0]);
                }
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} className="ml-1 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[200px] overflow-y-auto p-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <>
                {/* Clear option */}
                {value && (
                  <button
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent cursor-pointer"
                    onClick={handleClear}
                  >
                    <span className="italic">Clear vendor</span>
                  </button>
                )}

                {/* Vendor list */}
                {filtered.map((vendor) => (
                  <button
                    key={vendor.id}
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                    onClick={() => handleSelect(vendor)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        Number(value) === Number(vendor.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {vendor.name}
                  </button>
                ))}

                {/* Empty state / Create option */}
                {filtered.length === 0 && !search.trim() && (
                  <div className="py-4 text-center text-sm text-muted-foreground">No vendors found.</div>
                )}

                {/* Create new vendor */}
                {search.trim() && !hasExactMatch && (
                  <button
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-primary hover:bg-accent cursor-pointer"
                    onClick={handleCreateVendor}
                    disabled={createVendor.isPending}
                  >
                    {createVendor.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Create new vendor &quot;{search.trim()}&quot;
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
